// app/components/utils/letterUtils.js
"use client";

/* ============================== basic string utils ============================== */
export const toLower = (s) => String(s || "").toLowerCase().trim();

export function uniqKeepOrder(arr) {
  const seen = new Set();
  const out = [];
  for (const x of arr || []) {
    const k = toLower(x);
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(String(x).trim());
  }
  return out;
}

export function parseComma(s) {
  return uniqKeepOrder(
    String(s || "")
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean)
  );
}

export function parseSemicolon(s) {
  return uniqKeepOrder(
    String(s || "")
      .split(";")
      .map((t) => t.trim())
      .filter(Boolean)
  );
}

export function parseReasons(s) {
  return uniqKeepOrder(
    String(s || "")
      .split(/[\n;]+/g)
      .map((t) => t.trim())
      .filter(Boolean)
  );
}

export function intersectOptions(opts = [], picked = []) {
  const pickedSet = new Set((picked || []).map(toLower));
  return (opts || []).filter((o) => pickedSet.has(toLower(o)));
}

/* ============================== custom options persistence ============================== */
export function loadCustomList(k) {
  try {
    const arr = JSON.parse(localStorage.getItem(k) || "[]");
    return Array.isArray(arr) ? arr.filter(Boolean) : [];
  } catch {
    return [];
  }
}
export function saveCustomList(k, arr) {
  try {
    localStorage.setItem(k, JSON.stringify(arr || []));
  } catch {}
}

/* ============================== artifacts / blobs ============================== */
export const DOCX_MIME =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

export function base64ToBlob(b64, mime = DOCX_MIME) {
  if (!b64) return null;
  const bin = atob(b64);
  const len = bin.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

/* ============================== letter injection helpers ============================== */
const escapeRegex = (s = "") => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Try to place the counselor "experience" paragraph inside the letter body.
 * - Honors explicit markers if present.
 * - Otherwise inserts before common closings.
 * - As a last resort, appends to the end.
 */
export function injectExperienceIntoLetter(letterText = "", experience = "", form = {}) {
  const exp = String(experience || "").trim();
  if (!exp) return letterText;

  // Marker-based injection
  const markers = ["{{EXPERIENCE}}", "{{experience}}", "[[EXPERIENCE]]", "<EXPERIENCE/>", "<<EXPERIENCE>>"];
  for (const m of markers) {
    if (letterText.includes(m)) return letterText.replace(m, exp);
  }

  // If the experience already appears, skip
  const probe = exp.slice(0, Math.min(40, exp.length)).toLowerCase();
  if (probe && letterText.toLowerCase().includes(probe)) return letterText;

  // Try to inject before common closings
  const closers = uniqKeepOrder([
    String(form.closing || "").trim(),
    "Sincerely,",
    "Sincerely",
    "Best regards,",
    "Best regards",
    "Respectfully,",
    "Respectfully",
    "With appreciation,",
    "With appreciation",
  ]).filter(Boolean);

  for (const close of closers) {
    const idx = letterText.indexOf(close);
    if (idx > -1) {
      const before = letterText.slice(0, idx).replace(/\s*$/, "");
      const after = letterText.slice(idx);
      return `${before}\n\n${exp}\n\n${after}`;
    }
  }

  // Fallback: append
  return `${letterText.replace(/\s*$/, "")}\n\n${exp}\n`;
}

export function removeExperienceFromLetter(letterText = "", experience = "") {
  const exp = String(experience || "").trim();
  if (!exp) return letterText;
  const pattern = new RegExp(`\\n?\\s*${escapeRegex(exp)}\\s*\\n?`);
  return letterText.replace(pattern, "\n\n").replace(/\n{3,}/g, "\n\n");
}

/**
 * Insert a simple sport sentence near the top of the letter (after greeting),
 * honoring SPORT markers if present and avoiding duplication.
 */
export function injectSportIntoLetter(letterText = "", sport = "", student = "") {
  const s = String(sport || "").trim();
  if (!s) return letterText;

  if (letterText.includes("{{SPORT}}")) return letterText.replace("{{SPORT}}", s);
  if (letterText.includes("[[SPORT]]")) return letterText.replace("[[SPORT]]", s);

  if (letterText.toLowerCase().includes(s.toLowerCase())) return letterText;

  const sentence = `${student ? student : "The student"} participates in ${s}.`;

  const dearMatch = letterText.match(/(^|\n)Dear[^\n]*\n/i);
  if (dearMatch) {
    const at = (dearMatch.index ?? 0) + dearMatch[0].length;
    const before = letterText.slice(0, at);
    const after = letterText.slice(at);
    return `${before}\n${sentence}\n\n${after}`;
  }
  const firstBreak = letterText.indexOf("\n\n");
  if (firstBreak !== -1) {
    const before = letterText.slice(0, firstBreak);
    const after = letterText.slice(firstBreak);
    return `${before}\n\n${sentence}${after}`;
  }
  return `${letterText.replace(/\s*$/, "")}\n\n${sentence}\n`;
}

/* ============================== endorsement tail helper ============================== */
/* Match server behavior exactly: neutral tail, not exported as a constant */
const ENDORSEMENT_TAIL =
  "Their presence on campus will not only enhance the academic landscape but also inspire their peers to engage more deeply with the world around them. In conclusion, I strongly endorse this applicant for admission, confident in their ability to bring unique perspectives and a spirit of innovation to your college. Thank you for considering this outstanding individual.";

/**
 * Append ENDORSEMENT_TAIL to the provided paragraph, avoiding duplication and
 * ensuring proper punctuation/spacing. Mirrors server logic.
 *
 * @param {string} text - Existing counselor experience paragraph
 * @returns {string} - Experience paragraph with endorsement tail appended (once)
 */
export function addEndorsementTail(text = "") {
  const clean = String(text || "").trim();
  if (!clean) return ENDORSEMENT_TAIL;

  // If any distinctive part of the tail is already present, do nothing.
  const sentinel =
    "their presence on campus will not only enhance the academic landscape";
  if (clean.toLowerCase().includes(sentinel)) {
    return clean;
  }

  const needsPeriod = !/[.!?]"?$/.test(clean);
  const head = needsPeriod ? `${clean}.` : clean;
  return `${head} ${ENDORSEMENT_TAIL}`;
}
